import { PaginatedResponse, RequestParameters } from './types';
import { AxiosInstance, AxiosResponse } from 'axios';
import { axiosInstance } from './axios.ts';

export function snakeCaseToCamelCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/__/g, '_')
    .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
}

export function snakeCaseDictToCamelCaseDict(object: Record<string, any> | any | any[]): Record<string, any> {
  const newObj: Record<string, any> = {};

  if (!(object instanceof Array) && !(typeof object === 'object')) return object;

  if (object instanceof Array) return object.map((e: Record<any, any>) => snakeCaseDictToCamelCaseDict(e));

  Object.entries(object).forEach(([key, value]: [key: string, value: any]) => {
    if (value instanceof Array) value = value.map((e: Record<any, any>) => snakeCaseDictToCamelCaseDict(e));
    else if (typeof value === 'object' && value) value = snakeCaseDictToCamelCaseDict(value);
    newObj[snakeCaseToCamelCase(key)] = value;
  });

  return newObj;
}

export function snakeCaseResultToCamelCaseResult(result: Record<string, any>[]): Record<string, any>[] {
  return result.map((r) => snakeCaseDictToCamelCaseDict(r));
}

export function cleanCPFCNPJ(code: string): string {
  return code.replace(/\./g, '').replaceAll('-', '').replaceAll('/', '').replaceAll('_', '');
}

export function cleanCEP(code: string): string {
  return code.replaceAll('.', '').replaceAll('-', '').replaceAll('_', '');
}

export function urlWithParams(url: string, params: RequestParameters): string {
  if (!url.includes('?')) url += '?';
  return url + new URLSearchParams(params).toString();
}

export async function paginatedAxiosResponse<T>(response: Promise<AxiosResponse>): Promise<PaginatedResponse<T>> {
  return response.then(({ data }) => snakeCaseDictToCamelCaseDict(data) as PaginatedResponse<T>);
}

export function canSendDataToThirdParty<T>(
  data: string,
  desiredSize: number,
  callback: (cleanedData: string) => T
): T | undefined {
  const cleanedData = data.replaceAll('-', '').replaceAll('_', '').replaceAll('/', '');
  if (cleanedData.length !== desiredSize) return;

  return callback(cleanedData);
}

export async function getPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<PaginatedResponse<T>> {
  return paginatedAxiosResponse(using.get(urlWithParams(url, params)));
}

export async function getForcedPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<PaginatedResponse<T>> {
  return using.get(urlWithParams(url, params)).then(({ data }) => ({
    count: data.length,
    results: snakeCaseDictToCamelCaseDict(data) as T[],
  }));
}

export async function getNonPaginatedEndpoint<T>(
  url: string,
  params: RequestParameters = {},
  using: AxiosInstance = axiosInstance
): Promise<T[] | T> {
  return using.get(urlWithParams(url, params)).then(({ data }) => snakeCaseDictToCamelCaseDict(data) as T[]);
}
